import React from 'react'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { State, PartialAction } from 'src/types'
import {
  hamidashiObjs,
  stitchObjs,
  ysrLabelObjs,
  laceObjs,
  edgeColors,
  LEATHER_COLOR_BUTTON_OPTION,
  LACE_COLOR_BUTTON_OPTION,
  EDGE_COLOR_BUTTON_OPTION,
  STITCH_COLOR_BUTTON_OPTION,
  HAMIDASHI_COLOR_BUTTON_OPTION,
  LABEL_BUTTON_OPTION
} from 'src/features/yellStory/constants/colors'
import { useOriginDispatcher } from 'src/hooks/useOriginDispatcher'
import { dispatchObjectsGenerator, partsObjectsSwitcher } from 'src/features/yellStory/logic/colorSetting'
import { useDispatcher } from 'src/features/yellStory/logic/dispatchGenerator'
import { DISPATCHER } from 'src/features/yellStory/constants/actionType'
import { SelectCardWithImage } from 'src/Components/Ui/SelectCardWithImage'
import { useLocale } from 'src/locales/useLocale'

type Props = {
  state: State
  selectedIndex: number
  figurePanelNum: number
  dispatch: React.Dispatch<PartialAction>
}

export const ColorSetter: React.FC<Props> = ({ state, selectedIndex, figurePanelNum, dispatch }) => {
  const { t } = useLocale()
  const {
    parts,
    edge,
    lace,
    hamidashi,
    leatherIntegratedIndex,
    leatherIntegratedMiddle,
    leatherIntegratedRing,
    stitch,
    ysrLabel,
    ysrDirectLogo1Position,
    ...colorObjects
  } = state
  const partsKey = parts?.value
  const partsObjects = partsObjectsSwitcher(
    figurePanelNum,
    leatherIntegratedIndex.value,
    leatherIntegratedMiddle.value,
    leatherIntegratedRing.value,
    state?.webParts?.value
  )
  const { dispatchObjects, partsColorCaution } =
    dispatchObjectsGenerator(state?.series.value as 'newBestSelect' | 'newGoldLineNext' | 'newJuniorLine' | 'unselected') || {}
  const handle = {
    [partsKey]: useOriginDispatcher(DISPATCHER, partsKey, dispatch, dispatchObjects[partsKey]),
    parts: useOriginDispatcher(DISPATCHER, 'parts', dispatch, partsObjects),
    lace: useDispatcher('lace', dispatch),
    edge: useDispatcher('edge', dispatch),
    stitch: useDispatcher('stitch', dispatch),
    hamidashi: useDispatcher('hamidashi', dispatch),
    ysrLabel: useDispatcher('ysrLabel', dispatch)
  }
  if (!colorObjects || !partsObjects) return <></>
  const isDisabledLabel = ysrDirectLogo1Position.value === 'band'
  return (
    <TabPanel selectedIndex={selectedIndex} index={1}>
      <SelectCard
        summary={t.PARTS}
        selectedLabel={parts?.label}
        objects={partsObjects}
        handleChange={handle.parts}
        caution={'革カラー3色目以降、1色3,300円(税込)のオプションがかかります。'}
      />
      <SelectCard
        summary={`${parts?.label}カラー`}
        selectedLabel={colorObjects[partsKey]?.label}
        selectedColor={colorObjects[partsKey]?.color}
        objects={dispatchObjects[partsKey]}
        isError={colorObjects[partsKey]?.value === 'unselected'}
        handleChange={handle[partsKey]}
        caution={partsColorCaution}
        className={LEATHER_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={t.EDGE_COLOR}
        selectedLabel={edge.label}
        selectedColor={edge.color}
        objects={edgeColors}
        isError={edge?.value === 'unselected'}
        handleChange={handle.edge}
        defaultExpanded={false}
        className={EDGE_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={t.LACE}
        selectedLabel={lace.label}
        selectedColor={lace.color}
        objects={laceObjs}
        isError={lace?.value === 'unselected'}
        handleChange={handle.lace}
        defaultExpanded={false}
        className={LACE_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={t.STITCH}
        selectedLabel={stitch.label}
        selectedColor={stitch.color}
        objects={stitchObjs}
        isError={stitch?.value === 'unselected'}
        handleChange={handle.stitch}
        defaultExpanded={false}
        className={STITCH_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={t.HAMIDASHI}
        selectedLabel={hamidashi.label}
        selectedColor={hamidashi.color}
        objects={hamidashiObjs}
        isError={hamidashi?.value === 'unselected'}
        handleChange={handle.hamidashi}
        defaultExpanded={false}
        className={HAMIDASHI_COLOR_BUTTON_OPTION}
      />
      <SelectCardWithImage
        summary={t.LABEL}
        selectedLabel={ysrLabel.label}
        selectedColor={ysrLabel.color}
        objects={ysrLabelObjs}
        isError={ysrLabel?.value === 'unselected'}
        imageWidth={120}
        imageHeight={90}
        handleChange={handle.ysrLabel}
        disabled={isDisabledLabel}
        description={isDisabledLabel && 'ラベル指定したい場合、[刺繍設定/ダイレクトロゴ刺繍1]で"バンド"を解除してください。'}
        className={LABEL_BUTTON_OPTION}
      />
    </TabPanel>
  )
}
